import { useEffect, useRef } from "react";
import { C } from "../theme";

// Decorative "place-name rain" for the wide-screen gutters either side of the
// centered 900px content column. Char-stream matrix effect on <canvas>: each
// column spells out a street, city, or country name from around the world.
// No digits — names only. Purely cosmetic, canvas + rAF, no deps. Hidden on
// narrow screens and when prefers-reduced-motion is set.

// Streets, cities, and countries — a spread of names around the world.
const NAMES: string[] = [
  "Champs-Élysées", "Downing Street", "Brandenburger Tor", "Nevsky Prospekt",
  "Orchard Road", "Gran Vía", "Bahnhofstrasse", "Avenida Paulista",
  "Connaught Place", "Sukhumvit Road", "Karl Johans gate", "La Rambla",
  "Grafton Street", "Wenceslas Square", "Long Street", "Marina Bay",
  "Tokyo", "Berlin", "Reykjavík", "Cape Town", "São Paulo", "İstanbul",
  "Singapore", "Vancouver", "Dublin", "Praha", "Seoul", "Oslo",
  "København", "Zürich", "New Delhi", "Mexico City", "Cairo", "Sydney",
  "Marrakesh", "Lisboa", "Nairobi", "Hà Nội",
  "Iceland", "Japan", "Brazil", "Germany", "France", "Norway", "Thailand",
  "South Africa", "Canada", "Ireland", "Czechia", "South Korea", "Egypt",
  "Australia", "Switzerland", "India", "Portugal", "Kenya", "Vietnam",
];

function pick(i: number): string {
  return NAMES[((i % NAMES.length) + NAMES.length) % NAMES.length] + "    "; // trailing gap
}

function drift(canvas: HTMLCanvasElement, offset: number): () => void {
  const ctx = canvas.getContext("2d")!;
  const fontSize = 14;
  let cols = 0;
  let cells: { y: number; text: string; ti: number; speed: number; n: number; gen: number }[] =
    [];

  function resize() {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.font = `500 ${fontSize}px ${getComputedStyle(canvas).fontFamily}`;
    cols = Math.max(1, Math.floor(w / fontSize));
    cells = Array.from({ length: cols }, (_, i) => ({
      y: Math.floor((Math.sin(i * 99.7) * 0.5 + 0.5) * (h / fontSize)) * -1,
      text: pick(i + offset),
      ti: 0,
      speed: 0.25 + (Math.sin(i * 12.3) * 0.5 + 0.5) * 0.45,
      n: 0,
      gen: 0,
    }));
  }

  let raf = 0;
  function frame() {
    const h = canvas.clientHeight;
    // Fade prior frame toward bg for the trailing tail.
    ctx.fillStyle = "rgba(10,11,10,0.10)";
    ctx.fillRect(0, 0, canvas.clientWidth, h);

    const rows = h / fontSize;
    for (let i = 0; i < cells.length; i++) {
      const c = cells[i];
      c.n += c.speed;
      if (c.n < 1) continue;
      c.n = 0;

      const ch = c.text[c.ti % c.text.length];
      const x = i * fontSize;
      const y = c.y * fontSize;

      // Bright head, dimmer green body (relies on the fade rect for the trail).
      ctx.fillStyle = C.textBrightest;
      ctx.fillText(ch, x, y);
      ctx.fillStyle = "rgba(54,224,127,0.55)";
      ctx.fillText(ch, x, y - fontSize);

      c.y += 1;
      c.ti += 1;
      if (c.y > rows && Math.sin(i * 7.1 + c.ti) > 0.3) {
        c.y = 0;
        c.ti = 0;
        c.gen += 1;
        c.text = pick(i + offset + c.gen * 7); // fresh name each cycle
      }
    }
    raf = requestAnimationFrame(frame);
  }

  resize();
  window.addEventListener("resize", resize);
  raf = requestAnimationFrame(frame);
  return () => {
    cancelAnimationFrame(raf);
    window.removeEventListener("resize", resize);
  };
}

function Gutter({ side, offset }: { side: "left" | "right"; offset: number }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    if (!ref.current) return;
    return drift(ref.current, offset);
  }, [offset]);
  return (
    <canvas
      ref={ref}
      aria-hidden
      style={{
        position: "fixed",
        top: 0,
        [side]: 0,
        height: "100vh",
        // Gutter = half the space outside the 900px column, capped.
        width: "min(260px, calc((100vw - 900px) / 2))",
        zIndex: 0,
        pointerEvents: "none",
        fontFamily:
          "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
        // Fade out toward the content so it blends into the page.
        WebkitMaskImage: `linear-gradient(to ${
          side === "left" ? "right" : "left"
        }, #000 0%, #000 45%, transparent 100%)`,
        maskImage: `linear-gradient(to ${
          side === "left" ? "right" : "left"
        }, #000 0%, #000 45%, transparent 100%)`,
        opacity: 0.5,
      }}
    />
  );
}

export function MatrixRain() {
  const enabled = useRef(
    typeof window !== "undefined" &&
      window.matchMedia("(min-width: 1180px)").matches &&
      !window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );
  if (!enabled.current) return null;
  return (
    <>
      <Gutter side="left" offset={0} />
      <Gutter side="right" offset={29} />
    </>
  );
}
